import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { getActiveNetwork, getChainId, getChainIdHex, ACTIVE_NETWORK } from '../config/networks';
import {
  CONTRACT_ADDRESSES,
  ADRENALINE_PLAN_ABI,
  USER_STORAGE_ABI,
  NFT_ENGINE_ABI,
  ERC20_ABI
} from '../config/contract';
import { fetchBlacklist } from '../utils/blacklistApi';
import { isBlacklisted as checkBlacklisted, isAdminAddress } from '../config/admin';

const Web3Context = createContext();

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) throw new Error('useWeb3 must be used within Web3Provider');
  return context;
};

export const Web3Provider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  // planContract = main Plan contract (writes + config)
  // storageContract = UserStorage (user views)
  // engineContract = NFTEngine (nft views)
  // nftContract = alias to planContract (backwards compat: all write ops)
  const [planContract, setPlanContract] = useState(null);
  const [storageContract, setStorageContract] = useState(null);
  const [engineContract, setEngineContract] = useState(null);
  const [usdtContract, setUsdtContract] = useState(null);

  const [isConnecting, setIsConnecting] = useState(false);
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  // Plan config (caps + normal mode toggle). Fetched once on connect.
  const [planConfig, setPlanConfig] = useState({
    maxAutoInvestment: null,
    maxNormalInvestment: null,
    normalModeEnabled: true
  });

  // Address of the wallet that was rejected for being blacklisted.
  // When non-null, App.js renders the full-screen blacklist notice.
  const [blacklistedAddress, setBlacklistedAddress] = useState(null);

  const isSwitchingNetwork = useRef(false);
  const hasInitialized = useRef(false);

  const network = getActiveNetwork();
  const targetChainId = getChainId();
  const addresses = CONTRACT_ADDRESSES[ACTIVE_NETWORK];

  const checkNetwork = async (web3Provider) => {
    try {
      const net = await web3Provider.getNetwork();
      return net.chainId === targetChainId;
    } catch {
      return false;
    }
  };

  const switchNetwork = async () => {
    if (!window.ethereum || isSwitchingNetwork.current) return false;
    isSwitchingNetwork.current = true;

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: getChainIdHex() }]
      });
      isSwitchingNetwork.current = false;
      return true;
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: network.chainId,
              chainName: network.chainName,
              nativeCurrency: network.nativeCurrency,
              rpcUrls: network.rpcUrls,
              blockExplorerUrls: network.blockExplorerUrls
            }]
          });
          isSwitchingNetwork.current = false;
          return true;
        } catch (addError) {
          console.error('Failed to add network:', addError);
        }
      }
    }
    isSwitchingNetwork.current = false;
    return false;
  };

  const initContracts = async (signerInstance) => {
    try {
      const plan = new ethers.Contract(addresses.ADRENALINE_PLAN, ADRENALINE_PLAN_ABI, signerInstance);
      const usdt = new ethers.Contract(addresses.USDT, ERC20_ABI, signerInstance);

      // Discover UserStorage and NFTEngine addresses from the Plan contract
      const [storageAddr, engineAddr] = await Promise.all([
        plan.userStorage(),
        plan.nftEngine()
      ]);

      const storage = new ethers.Contract(storageAddr, USER_STORAGE_ABI, signerInstance);
      const engine = new ethers.Contract(engineAddr, NFT_ENGINE_ABI, signerInstance);

      setPlanContract(plan);
      setStorageContract(storage);
      setEngineContract(engine);
      setUsdtContract(usdt);

      // Fetch plan-level config (caps + normal mode flag) — static-ish data
      try {
        const [maxAuto, maxNormal, normEnabled] = await Promise.all([
          plan.maxAutoInvestment(),
          plan.maxNormalInvestment(),
          plan.normalModeEnabled()
        ]);
        setPlanConfig({
          maxAutoInvestment: maxAuto,
          maxNormalInvestment: maxNormal,
          normalModeEnabled: normEnabled
        });
      } catch (e) {
        console.error('Failed to fetch plan config', e);
      }

      return { plan, storage, engine, usdt };
    } catch (error) {
      console.error('Failed to init contracts:', error);
      return null;
    }
  };

  const fetchUserData = async (plan, storage, usdt, userAddress) => {
    try {
      const [balance, registered] = await Promise.all([
        usdt.balanceOf(userAddress),
        storage.isUserRegistered(userAddress)
      ]);

      setUsdtBalance(ethers.utils.formatUnits(balance, 18));
      setIsRegistered(registered);

      if (registered) {
        // New getUserDetails returns 13 fields
        const [details, income] = await Promise.all([
          storage.getUserDetails(userAddress),
          storage.getUserIncomeBreakdown(userAddress)
        ]);

        setUserDetails({
          sponsor: details.sponsor,
          totalInvested: details.totalInvested,
          totalEarned: details.totalEarned,
          principalBalance: details.principalBalance,
          pendingReinvest: details.pendingReinvest,
          autoInvested: details.autoInvested,
          normalInvested: details.normalInvested,
          directReferrals: details.directReferrals.toString(),
          directAutoUserBusiness: details.directAutoUserBusiness,
          totalNFTsPurchased: details.totalNFTsPurchased.toString(),
          isAutoUser: details.isAutoUser,
          isNormalUser: details.isNormalUser,
          isActive: details.isActive,
          // income fields (useful for Home/Profile to avoid double-fetch)
          totalSponsorIncome: income.sponsorIncome,
          totalLevelIncome: income.levelIncome,
          totalProfitIncome: income.profitIncome,
          totalAutoReferralIncome: income.autoReferralIncome
        });
      } else {
        setUserDetails(null);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  };

  const connectWallet = async () => {
    if (!window.ethereum) {
      alert('Please install MetaMask or Trust Wallet!');
      return;
    }

    if (isConnecting) return;
    setIsConnecting(true);

    try {
      const web3Provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });

      if (!accounts || accounts.length === 0) throw new Error('No accounts found');

      const correctNetwork = await checkNetwork(web3Provider);

      if (!correctNetwork) {
        const switched = await switchNetwork();
        if (!switched) {
          setIsConnecting(false);
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      const freshProvider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const signerInstance = freshProvider.getSigner();
      const userAddress = await signerInstance.getAddress();

      // Blacklist gate — block login before any state/contracts are set
      const blacklist = await fetchBlacklist();
      if (checkBlacklisted(userAddress, blacklist)) {
        setBlacklistedAddress(userAddress);
        setIsConnecting(false);
        setIsLoading(false);
        return;
      }
      setBlacklistedAddress(null);

      setProvider(freshProvider);
      setSigner(signerInstance);
      setAccount(userAddress);
      setIsCorrectNetwork(true);

      const contracts = await initContracts(signerInstance);
      if (contracts) {
        await fetchUserData(contracts.plan, contracts.storage, contracts.usdt, userAddress);
      }
    } catch (error) {
      console.error('Connection failed:', error);
    } finally {
      setIsConnecting(false);
      setIsLoading(false);
    }
  };

  const disconnectWallet = () => {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setPlanContract(null);
    setStorageContract(null);
    setEngineContract(null);
    setUsdtContract(null);
    setUsdtBalance('0');
    setIsCorrectNetwork(false);
    setIsRegistered(false);
    setUserDetails(null);
  };

  const refreshUserData = async () => {
    if (planContract && storageContract && usdtContract && account) {
      await fetchUserData(planContract, storageContract, usdtContract, account);
    }
  };

  useEffect(() => {
    if (!window.ethereum || hasInitialized.current) return;
    hasInitialized.current = true;

    const handleAccountsChanged = async (accounts) => {
      if (accounts.length === 0) {
        disconnectWallet();
      } else if (accounts[0] !== account) {
        // Re-run the blacklist gate on account switch
        const blacklist = await fetchBlacklist();
        if (checkBlacklisted(accounts[0], blacklist)) {
          disconnectWallet();
          setBlacklistedAddress(accounts[0]);
          return;
        }
        setBlacklistedAddress(null);
        setAccount(accounts[0]);
        if (planContract && storageContract && usdtContract) {
          await fetchUserData(planContract, storageContract, usdtContract, accounts[0]);
        }
      }
    };

    const handleChainChanged = async (chainIdHex) => {
      if (isSwitchingNetwork.current) return;
      const newChainId = parseInt(chainIdHex, 16);
      const isCorrect = newChainId === targetChainId;
      setIsCorrectNetwork(isCorrect);
      if (!isCorrect && account) await switchNetwork();
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    const checkConnection = async () => {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          await connectWallet();
        } else {
          setIsLoading(false);
        }
      } catch {
        setIsLoading(false);
      }
    };

    checkConnection();

    return () => {
      if (window.ethereum.removeListener) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, []);

  const dismissBlacklistNotice = () => setBlacklistedAddress(null);

  return (
    <Web3Context.Provider value={{
      account,
      provider,
      signer,
      // Split contracts
      planContract,
      storageContract,
      engineContract,
      // Backwards-compat alias: many pages still destructure `nftContract`
      nftContract: planContract,
      usdtContract,
      isConnecting,
      isCorrectNetwork,
      isLoading,
      usdtBalance,
      isRegistered,
      userDetails,
      planConfig,
      connectWallet,
      disconnectWallet,
      switchNetwork,
      refreshUserData,
      setIsRegistered,
      network,
      // Blacklist / admin
      blacklistedAddress,
      dismissBlacklistNotice,
      isAdmin: isAdminAddress(account),
    }}>
      {children}
    </Web3Context.Provider>
  );
};

export default Web3Context;
